// src/screens/Dashboard.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip,
  useTheme,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import { 
  Circle, 
  EuroSymbol, 
  AccessTime, 
  CalendarMonth, 
  Refresh, 
  InfoOutlined,
  NightsStay,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const StyledStatus = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
}));

const StatusIndicator = ({ status }) => {
  const theme = useTheme(); // Usa useTheme hook per accedere al tema
  const isActive = status === 'In corso';
  return (
    <StyledStatus>
      <Circle
        style={{
          color: isActive ? theme.palette.success.main : theme.palette.error.main,
          fontSize: '1rem',
        }}
      />
      <span>{status}</span>
    </StyledStatus>
  );
};

const StyledCard = styled(Card)(({ theme, color }) => ({
  height: '100%',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)',
  },
  position: 'relative',
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    backgroundColor: color || theme.palette.primary.main,
  },
}));

const StatCard = ({ title, value, icon, color }) => {
  return (
    <StyledCard color={color}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Box sx={{ 
            backgroundColor: `${color}22`, 
            borderRadius: '50%', 
            width: 40, 
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" component="div">
          {value}
        </Typography>
      </CardContent>
    </StyledCard>
  );
};

// Funzione per ottenere il primo giorno del mese
const getFirstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

// Funzione per ottenere l'ultimo giorno del mese
const getLastDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
};

const formatDateToLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Dashboard = () => {
  const [usageData, setUsageData] = useState([]);
  const [averageTime, setAverageTime] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
  });
  const [totalPrice, setTotalPrice] = useState(0);
  const [pitchUsageData, setPitchUsageData] = useState([]);
  const [recentStops, setRecentStops] = useState([]);
  const [preferredArrivalTimes, setPreferredArrivalTimes] = useState([]);
  const [startDate, setStartDate] = useState(
    formatDateToLocal(getFirstDayOfMonth())
  );
  const [endDate, setEndDate] = useState(
    formatDateToLocal(getLastDayOfMonth())
  );
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admins/dashboard-data`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate,
          endDate,
        },
      });

      setUsageData(response.data.usageData);
      setAverageTime(response.data.averageTime);
      setTotalPrice(response.data.totalPrice);
      setPitchUsageData(response.data.pitchUsageData);
      setRecentStops(response.data.recentStops);
      setPreferredArrivalTimes(response.data.preferredArrivalTimes);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartDateChange = (e) => {
    const value = e.target.value;
    const selectedDate = new Date(value);
    if (value && value.length === 10) {
      setStartDate(formatDateToLocal(selectedDate));
    }
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value;
    const selectedDate = new Date(value);
    if (value && value.length === 10) {
      setEndDate(formatDateToLocal(selectedDate));
    }
  };

  const COLORS = ['#4CAF50', '#2196F3', '#FFC107', '#FF5722', '#9C27B0', '#3F51B5'];

  const getStatus = (endTime) => {
    const now = Date.now() / 1000;
    return endTime > now ? 'In corso' : 'Scaduta';
  };

  const calculateDays = (startTime, endTime) => {
    const startDate = new Date(startTime * 1000);
    const endDate = new Date(endTime * 1000);
    const differenceInTime = endDate - startDate;
    return Math.ceil(differenceInTime / (1000 * 60 * 60 * 24));
  };

  const formatDate = (unixTime) => {
    const date = new Date(unixTime * 1000);
    return format(date, 'dd/MM/yyyy HH:mm', { locale: it });
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'bonifico':
        return 'Bonifico';
      case 'contanti':
        return 'Contanti';
      case 'carta':
        return 'Carta di Credito';
      default:
        return 'Non Specificato';
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  
    return (
      <g>
        <text x={cx} y={cy} dy={-10} textAnchor="middle" fill={fill} fontSize={14}>
          {payload.name}
        </text>
        <text x={cx} y={cy} dy={15} textAnchor="middle" fill="#333" fontSize={16} fontWeight="bold">
          {`${(percent * 100).toFixed(0)}%`}
        </text>
        <text x={cx} y={cy} dy={35} textAnchor="middle" fill="#666" fontSize={14}>
          {`${value} prenotazioni`}
        </text>
        <Pie
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          data={[{ name: payload.name, value: 1 }]}
        />
      </g>
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h4" sx={{ fontWeight: 500, color: 'text.primary' }}>
              Dashboard
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                label="Data Inizio"
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ width: 180 }}
              />
              <TextField
                label="Data Fine"
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ width: 180 }}
              />
              <Tooltip title="Aggiorna dati">
                <IconButton onClick={handleRefresh} color="primary" disabled={loading}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <StatCard 
            title="Tempo medio di sosta" 
            value={`${averageTime.days} ${averageTime.days === 1 ? 'giorno' : 'giorni'}`}
            icon={<AccessTime sx={{ color: '#2196F3' }} />}
            color="#2196F3"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <StatCard 
            title="Prezzo totale delle soste" 
            value={`€ ${totalPrice.toFixed(2)}`}
            icon={<EuroSymbol sx={{ color: '#4CAF50' }} />}
            color="#4CAF50"
          />
        </Grid>
        
        <Grid item xs={12} md={4}>
          <StatCard 
            title="Periodo visualizzato" 
            value={`${format(new Date(startDate), 'dd MMM', { locale: it })} - ${format(new Date(endDate), 'dd MMM yyyy', { locale: it })}`}
            icon={<CalendarMonth sx={{ color: '#FF9800' }} />}
            color="#FF9800"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Piazzola più utilizzata</Typography>
              <Tooltip title="Distribuzione delle prenotazioni per piazzola">
                <InfoOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
              </Tooltip>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={usageData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  activeShape={renderActiveShape}
                >
                  {usageData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value, name) => [`${value} prenotazioni`, name]} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Frequenza di prenotazione</Typography>
              <Tooltip title="Numero di prenotazioni per piazzola">
                <InfoOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
              </Tooltip>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={pitchUsageData}
                margin={{ top: 5, right: 30, left: 20, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={60} 
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  domain={[0, 'auto']}
                  tickFormatter={(tick) => (Number.isInteger(tick) ? tick : '')}
                  allowDecimals={false}
                />
                <RechartsTooltip formatter={(value) => [`${value} prenotazioni`]} />
                <Bar 
                  dataKey="value" 
                  fill="#4CAF50"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Orari di arrivo preferiti</Typography>
              <Tooltip title="Fascia oraria preferita per l'arrivo">
                <InfoOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
              </Tooltip>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={preferredArrivalTimes}
                margin={{ top: 5, right: 30, left: 20, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="time" />
                <YAxis tickCount={5} />
                <RechartsTooltip formatter={(value) => [`${value} arrivi`]} />
                <Bar 
                  dataKey="count" 
                  fill="#FF9800" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">Ultime 5 prenotazioni</Typography>
              <Chip 
                icon={<NightsStay fontSize="small" />} 
                label="Soste recenti" 
                variant="outlined" 
                color="primary" 
              />
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Cognome</TableCell>
                    <TableCell>Telefono</TableCell>
                    <TableCell>Targa</TableCell>
                    <TableCell>Piazzola</TableCell>
                    <TableCell>Inizio</TableCell>
                    <TableCell>Fine</TableCell>
                    <TableCell>Prezzo</TableCell>
                    <TableCell>Stato</TableCell>
                    <TableCell>Giorni</TableCell>
                    <TableCell>Pagamento</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentStops.length > 0 ? (
                    recentStops.map((stop) => (
                      <TableRow key={stop.id} hover>
                        <TableCell>{stop.users.firstName}</TableCell>
                        <TableCell>{stop.users.lastName}</TableCell>
                        <TableCell>{stop.users.phone}</TableCell>
                        <TableCell>
                          <Chip 
                            label={stop.vehicles?.licensePlate || 'N/A'} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{stop.restareas.friendlyName}</TableCell>
                        <TableCell>{formatDate(stop.start)}</TableCell>
                        <TableCell>{formatDate(stop.end)}</TableCell>
                        <TableCell>
                          <Typography fontWeight="600" color="primary.main">
                            € {stop.price}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusIndicator status={getStatus(stop.end)} />
                        </TableCell>
                        <TableCell align="center">
                          {calculateDays(stop.start, stop.end)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getPaymentMethodLabel(stop.paymentType)} 
                            size="small"
                            color={stop.paymentType === 'contanti' ? 'success' : 'primary'}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Typography variant="body2" color="textSecondary">
                          Nessuna prenotazione recente trovata
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;